import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { LANGUAGES } from '../utils/translations';
import { PageLayout } from '../components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  User, Crown, Heart, MessageSquare, 
  Shield, FileText, Trash2, LogOut, ChevronRight, Loader2, RefreshCw,
  Share2, BarChart3, ArrowDown, Globe, Download, Cookie, BookOpen, Wallet
} from 'lucide-react';
import { CURRENCIES } from '../utils/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Admin emails - founders/owners only
const ADMIN_EMAILS = [
  "diane.shawe@yahoo.com",
  "admin@mydirectdebits.com",
  "support@qualiquiz.app",
  "info@virtualautochat.uk"
];

export default function SettingsPage() {
  const { user, logout, deleteAccount, refreshUser } = useAuth();
  const { preference: currentLangPref, setPreference: setLangPreference } = useLanguage();
  const [langPref, setLangPrefState] = useState(currentLangPref);
  
  const setLangPref = (val) => {
    setLangPrefState(val);
    setLangPreference(val);
  };
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    checkSubscription();
  }, []);

  const checkSubscription = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/subscription/status`);
      setSubscription(data);
    } catch (err) {
      console.error('Error checking subscription:', err);
    }
  };

  const handleRestorePremium = async () => {
    setRestoring(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/subscription/restore`,
        {},
        
      );
      
      if (data.restored) {
        toast.success('Premium restored successfully!');
        await checkSubscription();
        await refreshUser();
      } else {
        toast.info(data.message);
      }
    } catch (err) {
      toast.error('Failed to restore premium');
    } finally {
      setRestoring(false);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      'Permanently delete your account?\n\nThis will permanently remove your account, all active debits, and your full cancelled-debits history. This cannot be undone.'
    );
    
    if (!confirmed) return;
    
    const doubleConfirm = window.confirm(
      'This is your final warning. All your direct debits and account data will be permanently deleted. Continue?'
    );
    
    if (!doubleConfirm) return;

    setDeleting(true);
    const result = await deleteAccount();
    
    if (result.success) {
      toast.success('Account deleted successfully');
      navigate('/');
    } else {
      toast.error(result.error || 'Failed to delete account');
      setDeleting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const isPremium = subscription?.tier === 'premium';

  const handleExportData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/direct-debits/export`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'my-direct-debits-export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded successfully');
    } catch (err) {
      toast.error('Failed to export data');
    }
  };

  const handleDowngrade = async () => {
    const debitCount = subscription?.debit_count || 0;
    let msg = 'Are you sure you want to downgrade to the free plan?';
    if (debitCount > 3) {
      msg = `Warning: You have ${debitCount} active reminders. Downgrading to free will deactivate ${debitCount - 3} of them (only 3 allowed on free). This cannot be undone. Continue?`;
    }
    if (!window.confirm(msg)) return;
    
    try {
      const { data } = await axios.post(`${API_URL}/api/subscription/downgrade`);
      if (data.deactivated_count > 0) {
        toast.success(`Downgraded to free. ${data.deactivated_count} reminder(s) were deactivated.`);
      } else {
        toast.success('Downgraded to free plan.');
      }
      checkSubscription();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to downgrade');
    }
  };
  
  // Check if user is admin
  const isAdmin = user && (
    ADMIN_EMAILS.includes(user.email?.toLowerCase()) || 
    user.is_admin
  );

  const SettingItem = ({ icon: Icon, label, onClick, rightContent, danger = false, "data-testid": testId }) => (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors ${
        danger ? 'text-alert' : 'text-text-primary'
      }`}
    >
      <div className="flex items-center gap-3">
        <Icon className={`w-5 h-5 ${danger ? 'text-alert' : 'text-text-secondary'}`} />
        <span className="font-medium">{label}</span>
      </div>
      {rightContent || <ChevronRight className="w-5 h-5 text-text-light" />}
    </button>
  );

  return (
    <PageLayout showNav showUpgrade={false} isPremium={isPremium}>
      <div className="px-4 py-6 max-w-lg mx-auto space-y-6">
        <h1 className="font-heading font-bold text-dark text-2xl">Settings</h1>

        {/* Profile Section */}
        <div className="card-default overflow-hidden">
          <div className="p-4 border-b border-border">
            <h2 className="text-xs font-semibold text-text-light uppercase tracking-wider">Profile</h2>
          </div>
          <div className="p-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-primary-light rounded-full flex items-center justify-center">
                <User className="w-7 h-7 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-dark">{user?.name}</p>
                <p className="text-sm text-text-secondary">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Currency Preference */}
        <div className="card-default overflow-hidden">
          <div className="p-4 border-b border-border">
            <h2 className="text-xs font-semibold text-text-light uppercase tracking-wider">Preferences</h2>
          </div>
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-text-secondary" />
                <span className="font-medium text-text-primary">Default Currency</span>
              </div>
              <select
                value={user?.default_currency || 'GBP'}
                onChange={async (e) => {
                  try {
                    await axios.put(`${API_URL}/api/auth/preferences`, { default_currency: e.target.value });
                    await refreshUser();
                    toast.success(`Currency set to ${e.target.value}`);
                  } catch (err) {
                    toast.error('Failed to update currency');
                  }
                }}
                data-testid="default-currency-select"
                className="rounded-xl px-3 py-2 text-sm border border-border bg-white text-dark font-medium"
              >
                {CURRENCIES.map(c => (
                  <option key={c.code} value={c.code}>{c.symbol} {c.code} — {c.name}</option>
                ))}
              </select>
            </div>

            {/* Estimated Balance Fields */}
            <div className="mt-5 pt-5 border-t border-border space-y-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-text-secondary" />
                  <span className="font-medium text-text-primary">App Language</span>
                </div>
                <select
                  value={langPref}
                  onChange={async (e) => {
                    const val = e.target.value;
                    setLangPref(val);
                    try {
                      await axios.put(`${API_URL}/api/auth/preferences`, { language: val });
                      await refreshUser();
                      toast.success('Language updated');
                    } catch {
                      toast.error('Failed to update language');
                    }
                  }}
                  data-testid="language-select"
                  className="rounded-xl px-3 py-2 text-sm border border-border bg-white text-dark font-medium"
                >
                  {LANGUAGES.map(l => (
                    <option key={l.code} value={l.code}>{l.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Balance Estimate Fields */}
            <div className="mt-5 pt-5 border-t border-border space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <Wallet className="w-4 h-4 text-text-secondary" />
                <span className="text-xs font-semibold text-text-light uppercase tracking-wider">Balance Estimate</span>
              </div>
              <p className="text-xs text-text-light">Used only to estimate your available balance. You can update this anytime.</p>

              <div>
                <label className="text-sm font-medium text-text-primary block mb-1">Total Monthly Income <span className="text-text-light font-normal">(optional)</span></label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  defaultValue={user?.monthly_income || ''}
                  onBlur={async (e) => {
                    try {
                      const val = e.target.value === '' ? null : e.target.value;
                      await axios.put(`${API_URL}/api/auth/preferences`, { monthly_income: val });
                      await refreshUser();
                      toast.success('Income updated');
                    } catch { toast.error('Failed to save'); }
                  }}
                  placeholder="e.g. 2500"
                  data-testid="monthly-income-input"
                  className="w-full rounded-xl px-3 py-2 text-sm border border-border bg-white text-dark"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-text-primary block mb-1">Date Paid Into Account <span className="text-text-light font-normal">(optional)</span></label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  defaultValue={user?.pay_date || ''}
                  onBlur={async (e) => {
                    try {
                      const val = e.target.value === '' ? null : e.target.value;
                      await axios.put(`${API_URL}/api/auth/preferences`, { pay_date: val });
                      await refreshUser();
                      toast.success('Pay date updated');
                    } catch { toast.error('Failed to save'); }
                  }}
                  placeholder="e.g. 25"
                  data-testid="pay-date-input"
                  className="w-full rounded-xl px-3 py-2 text-sm border border-border bg-white text-dark"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-text-primary block mb-1">Overdraft Balance <span className="text-text-light font-normal">(optional)</span></label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  defaultValue={user?.overdraft_balance || ''}
                  onBlur={async (e) => {
                    try {
                      const val = e.target.value === '' ? null : e.target.value;
                      await axios.put(`${API_URL}/api/auth/preferences`, { overdraft_balance: val });
                      await refreshUser();
                      toast.success('Overdraft updated');
                    } catch { toast.error('Failed to save'); }
                  }}
                  placeholder="e.g. 500"
                  data-testid="overdraft-input"
                  className="w-full rounded-xl px-3 py-2 text-sm border border-border bg-white text-dark"
                />
              </div>

              <p className="text-xs text-text-light italic">This is only an estimate. Actual bank balances may differ. MDD does not connect to your bank.</p>
            </div>
          </div>
        </div>

        {/* Upgrade Card (for free users) */}
        {!isPremium && (
          <Link 
            to="/upgrade"
            className="block card-premium p-5"
            data-testid="upgrade-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gold rounded-full flex items-center justify-center">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-dark">Upgrade to Premium</p>
                <p className="text-sm text-text-secondary">Unlimited direct debits</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gold" />
            </div>
          </Link>
        )}

        {/* Subscription Section */}
        <div className="card-default overflow-hidden">
          <div className="p-4 border-b border-border">
            <h2 className="text-xs font-semibold text-text-light uppercase tracking-wider">Subscription</h2>
          </div>
          <SettingItem
            icon={RefreshCw}
            label="Restore Premium"
            onClick={handleRestorePremium}
            rightContent={
              restoring ? (
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
              ) : (
                <ChevronRight className="w-5 h-5 text-text-light" />
              )
            }
          />
          {isPremium && (
            <SettingItem
              icon={ArrowDown}
              label="Downgrade to Free"
              onClick={handleDowngrade}
              danger
            />
          )}
        </div>

        {/* Admin Section - Only visible to admins */}
        {isAdmin && (
          <div className="card-default overflow-hidden">
            <div className="p-4 border-b border-border bg-primary-light">
              <h2 className="text-xs font-semibold text-primary uppercase tracking-wider">Admin</h2>
            </div>
            <SettingItem
              icon={Share2}
              label="Recommend, Embed or Share"
              onClick={() => navigate('/share')}
            />
            <SettingItem
              icon={BarChart3}
              label="View Analytics"
              onClick={() => navigate('/admin')}
            />
            <SettingItem
              icon={BookOpen}
              label="Blog Management"
              onClick={() => navigate('/admin/blog')}
              data-testid="admin-blog-btn"
            />
            <SettingItem
              icon={FileText}
              label="Technical Specification"
              onClick={() => navigate('/admin/technical-spec')}
              data-testid="admin-tech-spec-btn"
            />
          </div>
        )}

        {/* Support Section */}
        <div className="card-default overflow-hidden">
          <div className="p-4 border-b border-border">
            <h2 className="text-xs font-semibold text-text-light uppercase tracking-wider">Support</h2>
          </div>
          <SettingItem
            icon={Heart}
            label="Financial Support & Grants"
            onClick={() => navigate('/support')}
          />
          <SettingItem
            icon={MessageSquare}
            label="Send Feedback"
            onClick={() => navigate('/feedback')}
          />
        </div>

        {/* Legal Section */}
        <div className="card-default overflow-hidden">
          <div className="p-4 border-b border-border">
            <h2 className="text-xs font-semibold text-text-light uppercase tracking-wider">Legal</h2>
          </div>
          <SettingItem
            icon={Shield}
            label="Privacy Policy"
            onClick={() => navigate('/privacy')}
          />
          <SettingItem
            icon={FileText}
            label="Terms of Service"
            onClick={() => navigate('/terms')}
          />
          <SettingItem
            icon={Cookie}
            label="Cookie Policy"
            onClick={() => navigate('/cookies')}
            data-testid="cookie-policy-btn"
          />
        </div>

        {/* Account Section */}
        <div className="card-default overflow-hidden">
          <div className="p-4 border-b border-border">
            <h2 className="text-xs font-semibold text-text-light uppercase tracking-wider">Account</h2>
          </div>
          <SettingItem
            icon={Download}
            label="Export My Data"
            onClick={handleExportData}
            data-testid="export-data-btn"
          />
          <SettingItem
            icon={Trash2}
            label="Delete Account"
            onClick={handleDeleteAccount}
            danger
            rightContent={
              deleting ? (
                <Loader2 className="w-5 h-5 animate-spin text-alert" />
              ) : (
                <ChevronRight className="w-5 h-5 text-alert" />
              )
            }
          />
        </div>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full card-default p-4 flex items-center justify-center gap-2 text-alert font-medium hover:bg-alert-light transition-colors"
        >
          <LogOut className="w-5 h-5" />
          Log Out
        </button>

        {/* App Version */}
        <p className="text-center text-text-light text-xs">
          My Direct Debits v1.0.0
        </p>
      </div>
    </PageLayout>
  );
}
